import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import StorefrontAPI from 'utilities/storefrontApi';
import { loadSession } from 'utilities/storefrontSession';
import * as Cart from 'utilities/storefrontCart';
import { pushRecent } from 'utilities/storefrontRecentlyViewed';
import { SHELL, MAX, SUB_INPUT, BUTTON, brandPath, formatMoney } from './styles';
import useBrand from './useBrand';
import useSeoMeta from './useSeoMeta';
import StorefrontNav from './StorefrontNav';
import Breadcrumbs from './Breadcrumbs';
import ProductGallery from './ProductGallery';
import VariantSwatches from './VariantSwatches';
import ProductReviews from './ProductReviews';
import RecentlyViewed from './RecentlyViewed';
import { ShellMessage } from './shellHelpers';

/**
 * Product detail page.
 *
 * Resolves the product by slug (or _id if the slug looks like a Mongo
 * ObjectId — accepts both so old links don't 404). Adds the visit to the
 * "recently viewed" rail and hydrates wishlist state if the shopper is
 * signed in. SEO + social cards via useSeoMeta. Sticky add-to-cart bar
 * on mobile so the primary action stays reachable.
 */
export default function StorefrontProduct() {
  const { productSlug } = useParams();
  const { brand, error } = useBrand();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariantId, setSelectedVariantId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const [inWishlist, setInWishlist] = useState(false);
  const [wishlistBusy, setWishlistBusy] = useState(false);
  const accent = brand?.brand_color || '#422AFB';

  // SEO + social cards. Prefer the product's own seo_* fields when the
  // owner filled them in; fall back to name + description.
  const primaryImage = product?.images?.find((i) => i.is_primary) || product?.images?.[0];
  useSeoMeta(brand && product ? {
    title: `${product.seo_title || product.name} — ${brand.name}`,
    description: (product.seo_description || product.description || '').slice(0, 200),
    image: primaryImage?.url || brand.logo_upload_path || '',
    type: 'product',
  } : {});

  useEffect(() => {
    if (!brand || !productSlug) return;
    setLoading(true);
    const query = /^[a-f0-9]{24}$/i.test(productSlug)
      ? { _id: productSlug }
      : { slug: productSlug };
    StorefrontAPI.getProduct(brand._id, query)
      .then((p) => {
        setProduct(p);
        if (p?.variants?.length) setSelectedVariantId(p.variants[0]._id);
        if (p?._id) pushRecent(brand._id, p._id);
        if (p?._id && loadSession(brand._id)?.token) {
          StorefrontAPI.wishlistContains(brand._id, p._id)
            .then((res) => setInWishlist(!!res?.in_wishlist))
            .catch(() => { /* ignore */ });
        }
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [brand, productSlug]);

  const toggleWish = async () => {
    if (!brand?._id || !product?._id) return;
    if (!loadSession(brand._id)?.token) {
      navigate(brandPath(brand, '/login'));
      return;
    }
    setWishlistBusy(true);
    try {
      const res = await StorefrontAPI.toggleWishlist(brand._id, product._id);
      setInWishlist(!!res?.in_wishlist);
    } catch (_) { /* UI will reflect server state on next visit */ }
    finally { setWishlistBusy(false); }
  };

  const variant = useMemo(
    () => product?.variants?.find((v) => String(v._id) === String(selectedVariantId)),
    [product, selectedVariantId]
  );

  const price = useMemo(
    () => variant?.price != null ? variant.price : product?.base_price,
    [variant, product]
  );

  const handleAdd = () => {
    if (!brand || !product || !variant) return;
    const attrs = variant.attributes || {};
    const variantName = Object.entries(attrs).map(([k, v]) => `${k}: ${v}`).join(' · ');
    Cart.addToCart(brand._id, {
      product_id: product._id,
      variant_id: variant._id,
      sku: variant.sku,
      name: product.name,
      variant_name: variantName,
      unit_price: Number(price || 0),
      currency: variant.currency || product.currency || 'USD',
      image_url: variant.image_url || product.images?.[0]?.url || '',
      quantity: Number(quantity || 1),
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1600);
  };

  if (error) return <ShellMessage>{error}</ShellMessage>;
  if (!brand || loading) return <ShellMessage>Loading…</ShellMessage>;
  if (!product) return <ShellMessage>Product not found.</ShellMessage>;

  const breadcrumbItems = [
    { label: 'Home', to: brandPath(brand, '') },
    { label: 'Shop', to: brandPath(brand, '/shop') },
    ...(product.category
      ? [{ label: product.category, to: `${brandPath(brand, '/shop')}?category=${encodeURIComponent(product.category)}` }]
      : []),
    { label: product.name },
  ];

  return (
    <div style={SHELL}>
      <StorefrontNav brand={brand} />
      <div style={MAX}>
        <Breadcrumbs items={breadcrumbItems} />
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 32 }}>
          <ProductGallery product={product} accent={accent} />
          <div>
            <h1 style={{ margin: '0 0 6px', fontSize: 28 }}>{product.name}</h1>
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 18 }}>
              {formatMoney(price, variant?.currency || product.currency)}
            </div>
            {product.description && (
              <p style={{ whiteSpace: 'pre-wrap', color: '#475569', marginBottom: 18 }}>{product.description}</p>
            )}

            {product.variants?.length > 1 && (
              <VariantSwatches
                variants={product.variants}
                options={product.options}
                selectedVariantId={selectedVariantId}
                onSelect={setSelectedVariantId}
                accent={accent}
              />
            )}

            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase' }}>Quantity</div>
              <input
                type='number'
                min='1'
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, Number(e.target.value || 1)))}
                style={{ ...SUB_INPUT, maxWidth: 120 }}
              />
            </div>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <button onClick={handleAdd} style={BUTTON(accent)} disabled={!variant}>
                Add to cart
              </button>
              <button
                type='button'
                onClick={toggleWish}
                disabled={wishlistBusy}
                title={inWishlist ? 'Remove from wishlist' : 'Save for later'}
                style={{
                  padding: '0 14px', borderRadius: 8,
                  border: `1px solid ${inWishlist ? '#dc2626' : '#e5e7eb'}`,
                  background: 'white',
                  color: inWishlist ? '#dc2626' : '#475569',
                  fontWeight: 600, fontSize: 14,
                  cursor: 'pointer', height: 44,
                  opacity: wishlistBusy ? 0.6 : 1,
                }}
              >
                {inWishlist ? '♥ Saved' : '♡ Save'}
              </button>
              <button onClick={() => navigate(brandPath(brand, '/cart'))} style={BUTTON(accent, true)}>
                View cart
              </button>
              {added && <span style={{ color: '#16a34a', fontSize: 13, fontWeight: 600 }}>Added ✓</span>}
            </div>
          </div>
        </div>

        <ProductReviews brand={brand} product={product} accent={accent} />
        <RecentlyViewed brand={brand} excludeId={product._id} />

        {/* Spacer so the mobile sticky bar doesn't overlap the last bit
            of content. */}
        <div className='tpo-mobile-cart-spacer' />
      </div>

      <style>{`
        .tpo-mobile-cart-bar, .tpo-mobile-cart-spacer { display: none; }
        @media (max-width: 720px) {
          .tpo-mobile-cart-bar { display: flex; }
          .tpo-mobile-cart-spacer { display: block; height: 76px; }
        }
      `}</style>
      <div
        className='tpo-mobile-cart-bar'
        style={{
          position: 'fixed',
          left: 0, right: 0, bottom: 0,
          background: '#ffffff',
          borderTop: '1px solid #e5e7eb',
          padding: '10px 14px',
          alignItems: 'center', justifyContent: 'space-between',
          gap: 10, zIndex: 20,
          boxShadow: '0 -8px 24px rgba(15, 23, 42, 0.08)',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontWeight: 700, fontSize: 14, color: '#0f172a',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {product.name}
          </div>
          <div style={{ fontSize: 13, color: '#475569' }}>
            {formatMoney(price, variant?.currency || product.currency)}
          </div>
        </div>
        <button
          onClick={handleAdd}
          disabled={!variant}
          style={{
            background: accent, color: 'white',
            border: 'none', padding: '10px 16px',
            borderRadius: 10, fontWeight: 700, fontSize: 14,
            cursor: variant ? 'pointer' : 'not-allowed',
            opacity: variant ? 1 : 0.6,
            whiteSpace: 'nowrap',
          }}
        >
          {added ? 'Added ✓' : 'Add to cart'}
        </button>
      </div>
    </div>
  );
}
